import copy from "fast-copy";
import * as is from 'utilities/is';
import { CONDITIONS } from 'constants';
import * as Contexts from 'constants/context';
import { notEmpty, unique } from 'utilities/list';
import { APPLICATION_EVENT } from 'constants/events';
import { ContextManager } from './context_manager';
import RecursiveIterator from 'recursive-iterator';
import { collectTemplateStates } from 'utilities/parsers';

// TODO - generate dynamically from config
const VARIABLE_CONFIGS = ['annotations', 'display.layout'];
const BROADCAST_ID = 'StateEngine';

// convert config key to tree path
export const getVariablePath = key => {
    const path = VARIABLE_CONFIGS.find(path => path.split('.').includes(key));
    return path && path.split('.');
}

export const hasVariation = config => config.variations && config.variations.length;

export class StateEngine {
    constructor({ eventBus, config = {} }) {
        this.eventBus = eventBus;
        this.lastState = {};
        this.lockedContext = {};
        this.contextManager = new ContextManager({ eventBus, initialContext: { ...config.state, variables: config.variables } });
        this.contextManager.onContextChange(this.onContextChange);
        this.configuration = config;
        this.eventBus.on(APPLICATION_EVENT.UPDATED, this.onApplicationUpdate);
        this.eventBus.on(APPLICATION_EVENT.LOCK_STATE, this.onLockState);
        this.eventBus.on(APPLICATION_EVENT.UNLOCK_STATE, this.onUnlockState);
    }

    applyContext() {
        let applied = (
            Object
                .keys(this.activeConfig)
                .reduce((applied, key) => {
                    const path = getVariablePath(key);

                    if (path) {
                        let appliedConf = applied;

                        path.forEach((p,i) => {
                            if (i === path.length - 1) { // terminal node
                                // apply variations based on state
                                appliedConf[p] = this.applyVariations(appliedConf[p]);
                            } else { // walk tree
                                appliedConf = appliedConf[p];
                            }
                        })
                    }

                    return applied;
                }, copy(this.activeConfig))
        );

        applied = this.compileVariables(applied);

        return applied;
    }

    applyVariations(configItem = []) {
        if (!is.array(configItem)) {
            configItem = [configItem];
        }

        for (let i = 0, len = configItem.length; i < len; i++) {
            if (configItem[i].variations && configItem[i].variations.length) {
                for (let j = 0, jLen = configItem[i].variations.length; j < jLen; j++) {
                    let conditionsMet = true,
                        conditions = configItem[i].variations[j].conditions;

                    for (let k = 0, kLen = conditions.length; k < kLen; k++) {
                        conditionsMet = conditionsMet && this.assessCondition(conditions[k]);
                    }

                    if (conditionsMet) {
                        this.applyChanges(configItem[i], configItem[i].variations[j].changes);
                    }
                }
            }
        }

        return configItem;
    }

    applyChanges(subject = {}, changes = {}) {
        Object
            .keys(changes)
            .forEach(changeKey => {
                const existing = subject[changeKey]
                const change = changes[changeKey];

                if (change !== null && typeof change === 'object' && !Array.isArray(change)) {
                    subject[changeKey] = { ...existing, ...change };
                } else if (Array.isArray(change) && Array.isArray(existing)) {
                    subject[changeKey] = [
                        ...existing,
                        ...change
                    ];
                } else {
                    subject[changeKey] = change;
                }
            })
    }

    compileVariables(config = {}) {
        const iterator = new RecursiveIterator(config, 0, true, 6);

        for (let { key, node, parent } of iterator) {
            if (is.string(node)) {
                parent[key] = this.contextManager.compileContext(node);
            }
        }

        return config;
    }

    assessCondition(condition, activeState) {
        activeState = activeState || { ...this.activeState, ...this.lockedContext };

        const state = activeState[condition.state],
            value = condition.value,
            { assertion = 'equal' } = condition;

        if (is.object(value)) {
            // verify conditions on sub nodes
            return (
                Object
                    .keys(value)
                    .every(key => this.assessCondition({ assertion, state: key, value: value[key] }, state))
            );
        }

        if (assertion === CONDITIONS.EQUAL) return state === value;
        else if (assertion === CONDITIONS.LESS_THAN) return state < value;
        else if (assertion === CONDITIONS.GREATER_THAN) return state > value;
        else if (assertion === CONDITIONS.INCLUDES) return state && state.includes(value);

        return true;
    }

    publishStateChange() {
        this.statefulConfiguration = this.applyContext();
        const emitter = BROADCAST_ID
        const state = { ...this.activeState, ...this.lockedContext };
        this.eventBus.emit(APPLICATION_EVENT.STATE_CHANGE, { ...this.statefulConfiguration, state, emitter });
    }

    onContextChange = (name, value) => {
        if (is.equal(this.activeState[name],value)) {
            return;
        }

        this.lastState[name] = this.activeState[name];
        this.activeState[name] = value;
        this.publishStateChange();
    }

    onApplicationUpdate = ({ state = {}, emitter }) => {
        if (emitter === BROADCAST_ID) {
            // Don't respond to updates the StateEngine broadcasts
            // Prevents State Engine -> EventBus loops
            return;
        }

        // check context change after other handlers have finished
        requestAnimationFrame(
            () => Object.keys(state).forEach(key => this.onContextChange(key, state[key]))
        );
    }

    onLockState = (stateName, value) => {
        if (this.lockedContext[stateName] === value) {
            return;
        }

        this.lockedContext[stateName] = value;
        this.publishStateChange();
    }

    onUnlockState = stateName => {
        if (!this.lockedContext[stateName]) {
            return;
        }

        delete this.lockedContext[stateName];
        this.publishStateChange();
    }

    getWatchableContexts(config = {}) {
        return (
            Object
                .keys(config)
                .reduce((contexts, key) => {
                    const path = getVariablePath(key);

                    if (path) {
                        const 
                            variableNode = path.reduce((r,p) => r[p], config),
                            conditionalStates = (
                                variableNode
                                    .filter(hasVariation)
                                    .flatMap(c => c.variations.flatMap(v => v.conditions.flatMap(c => c.state)))
                            ),
                            templatizedContent = variableNode.flatMap(node => collectTemplateStates(node)).filter(notEmpty).map(s => s.split('.').shift());

                        contexts = contexts.concat(conditionalStates).concat(templatizedContent).filter(unique);
                    }

                    return contexts;
                }, [])
        )
    }

    set configuration(config = {}) {
        this.activeConfig = copy(config);
        this.contextManager.activeContexts = this.getWatchableContexts(config);
        this.activeState = { ...this.contextManager.currentState };
        this.statefulConfiguration = this.applyContext();
    }

    get variables() {
        return this.contextManager.contexts[Contexts.VARIABLES].state || {}; 
    }
}